import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FaEnvelope, FaPaperPlane, FaTrash, FaSearch, FaPlus, FaTimes,
  FaSpinner, FaBuilding, FaLifeRing, FaUser
} from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import { API_URL } from '../config';

const authHeaders = (extra = {}) => ({
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
  ...extra
});

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

function ClientMessages({ onLogout }) {
  const [user] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [messages, setMessages] = useState([]);
  const [team, setTeam] = useState({ company_name: null, company_people: [], admins: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ recipient: '', subject: '', message: '' });

  useEffect(() => {
    loadMessages();
    loadTeam();
  }, []);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/messages?user_id=${user.id}`, { headers: authHeaders() });
      const data = await r.json();
      if (data.success) setMessages(data.messages || []);
    } catch (err) {
      console.error('Messages error:', err);
      toast.error('Could not load your messages');
    } finally {
      setLoading(false);
    }
  };

  const loadTeam = async () => {
    try {
      // Who I may message: One Purpose Logistics + my own company's people.
      const r = await fetch(`${API_URL}/client/team`, { headers: authHeaders() });
      const data = await r.json();
      if (data.success) setTeam({
        company_name: data.company_name,
        company_people: data.company_people || [],
        admins: data.admins || []
      });
    } catch (err) {
      console.error('Team error:', err);
    }
  };

  const markRead = async (msg) => {
    if (!msg || msg.is_read) return;
    try {
      await fetch(`${API_URL}/messages/${msg.id}/read`, { method: 'PUT', headers: authHeaders() });
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, is_read: true } : m)));
    } catch (err) {
      console.error(err);
    }
  };

  const openMessage = (msg) => {
    setSelected(msg);
    setReplyText('');
    markRead(msg);
  };

  const send = async ({ recipient_id, subject, message, parent_message_id }) => {
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/messages`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ recipient_id, subject, message, parent_message_id: parent_message_id || null })
      });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || 'Could not send the message');
      toast.success('✅ Message sent');
      await loadMessages();
      return true;
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Could not send the message');
      return false;
    } finally {
      setSending(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selected) return;
    const toId = selected.sender_id === user.id ? selected.recipient_id : selected.sender_id;
    const ok = await send({
      recipient_id: toId,
      subject: selected.subject?.startsWith('Re:') ? selected.subject : `Re: ${selected.subject}`,
      message: replyText,
      parent_message_id: selected.id
    });
    if (ok) { setReplyText(''); setSelected(null); }
  };

  const sendCompose = async (e) => {
    e.preventDefault();
    if (!compose.recipient) { toast.error('Choose who the message goes to'); return; }
    if (!compose.subject.trim() || !compose.message.trim()) { toast.error('Add a subject and a message'); return; }
    const ok = await send({ recipient_id: compose.recipient, subject: compose.subject, message: compose.message });
    if (ok) {
      setComposeOpen(false);
      setCompose({ recipient: '', subject: '', message: '' });
    }
  };

  const remove = async (msg) => {
    try {
      const r = await fetch(`${API_URL}/messages/${msg.id}?user_id=${user.id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await r.json();
      if (data.success) {
        setMessages((prev) => prev.filter((m) => m.id !== msg.id));
        if (selected && selected.id === msg.id) setSelected(null);
        toast.success('Message removed from your inbox');
      }
    } catch (err) {
      console.error(err);
      toast.error('Could not delete the message');
    }
  };

  const visible = messages.filter((m) => {
    if (!search.trim()) return true;
    const t = search.trim().toLowerCase();
    return [m.subject, m.message, m.sender?.full_name, m.recipient?.full_name].join(' ').toLowerCase().includes(t);
  });

  const unread = messages.filter((m) => !m.is_read && m.recipient_id === user.id).length;
  const other = (m) => (m.sender_id === user.id ? m.recipient : m.sender);

  return (
    <ClientLayout activeTab="messages" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>✉️ Messages</h1>
          <p className="text-sm mt-1" style={{ color: '#4A5568' }}>
            Message the One Purpose Logistics team, or colleagues at {team.company_name || 'your company'}.
            {unread > 0 ? ` · ${unread} unread` : ''}
          </p>
        </div>
        <button
          onClick={() => setComposeOpen(true)}
          className="flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-semibold"
          style={{ backgroundColor: '#00A86B' }}
        >
          <FaPlus size={12} /> New message
        </button>
      </div>

      <div className="bg-white rounded-xl border p-4 mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search your messages..."
            className="w-full pl-9 pr-4 py-2 rounded-lg border text-sm focus:outline-none"
            style={{ borderColor: '#E8ECEF' }}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-3xl mx-auto mb-4" style={{ color: '#00A86B' }} />
            <p className="text-gray-500">Loading your messages...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E8ECEF' }}>
            {visible.length === 0 ? (
              <div className="p-16 text-center">
                <FaEnvelope className="text-4xl mx-auto mb-4" style={{ color: '#CBD5E1' }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#0A1929' }}>No messages yet</h3>
                <p className="text-sm text-gray-500">
                  Start a conversation with your One Purpose account manager or a colleague — click New message.
                </p>
              </div>
            ) : (
              <div className="divide-y" style={{ borderColor: '#F1F5F9' }}>
                {visible.map((m) => {
                  const person = other(m);
                  const mine = m.sender_id === user.id;
                  const isUnread = !m.is_read && m.recipient_id === user.id;
                  const fromStaff = person && ['admin', 'super_admin'].includes(person.role);
                  return (
                    <button
                      key={m.id}
                      onClick={() => openMessage(m)}
                      className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors"
                      style={selected && selected.id === m.id ? { backgroundColor: '#f0fdf4' } : undefined}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 font-semibold text-sm min-w-0" style={{ color: '#0A1929' }}>
                          {isUnread ? <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#00A86B' }} /> : null}
                          <span className="truncate">{m.subject}</span>
                        </span>
                        <span className="text-[11px] whitespace-nowrap" style={{ color: '#94A3B8' }}>{fmtDateTime(m.created_at)}</span>
                      </div>
                      <p className="text-xs mt-1 truncate" style={{ color: '#4A5568' }}>
                        {mine ? 'To ' : 'From '}
                        {person?.full_name || person?.email || 'unknown'}
                        {fromStaff ? ' · One Purpose Logistics' : (mine ? '' : ` · ${team.company_name || 'your company'}`)}
                      </p>
                      <p className="text-xs mt-1 truncate" style={{ color: '#94A3B8' }}>{m.message}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border" style={{ borderColor: '#E8ECEF' }}>
            {!selected ? (
              <div className="p-16 text-center">
                <p className="text-sm" style={{ color: '#94A3B8' }}>Select a message to read it and reply.</p>
              </div>
            ) : (
              <div className="p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold" style={{ color: '#0A1929' }}>{selected.subject}</h3>
                    <p className="text-xs mt-1" style={{ color: '#4A5568' }}>
                      From {selected.sender?.full_name || selected.sender?.email}
                      {selected.sender && ['admin', 'super_admin'].includes(selected.sender.role) ? ' · One Purpose Logistics' : ''}
                      {' · to '}{selected.recipient?.full_name || selected.recipient?.email}
                      {' · '}{fmtDateTime(selected.created_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => remove(selected)}
                    title="Delete"
                    className="w-8 h-8 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 flex items-center justify-center flex-shrink-0"
                  >
                    <FaTrash size={13} />
                  </button>
                </div>

                <p className="text-sm whitespace-pre-line mb-5" style={{ color: '#0A1929' }}>{selected.message}</p>

                <form onSubmit={sendReply} className="border-t pt-4" style={{ borderColor: '#E8ECEF' }}>
                  <label className="block text-xs uppercase tracking-wide mb-1" style={{ color: '#94A3B8' }}>Reply</label>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={3}
                    placeholder="Type your reply..."
                    className="w-full px-3 py-2 border rounded-lg text-sm mb-3 focus:outline-none"
                    style={{ borderColor: '#E8ECEF' }}
                  />
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setSelected(null)} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50" style={{ borderColor: '#E8ECEF' }}>
                      Close
                    </button>
                    <button
                      type="submit"
                      disabled={sending}
                      className="flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                      style={{ backgroundColor: '#00A86B' }}
                    >
                      {sending ? <FaSpinner className="animate-spin" size={12} /> : <FaPaperPlane size={12} />} Send reply
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

        </div>
      )}

      {composeOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(10,25,41,0.55)' }}
          onClick={() => setComposeOpen(false)}
        >
          <div className="bg-white rounded-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#E8ECEF' }}>
              <h3 className="font-semibold" style={{ color: '#0A1929' }}>New message</h3>
              <button
                onClick={() => setComposeOpen(false)}
                className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 flex items-center justify-center"
              >
                <FaTimes size={13} />
              </button>
            </div>
            <form onSubmit={sendCompose} className="p-5">
              <label className="block text-xs uppercase tracking-wide mb-1" style={{ color: '#94A3B8' }}>To</label>
              <select
                value={compose.recipient}
                onChange={(e) => setCompose((c) => ({ ...c, recipient: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm mb-4 focus:outline-none"
                style={{ borderColor: '#E8ECEF' }}
              >
                <option value="">Choose a recipient...</option>
                <optgroup label="One Purpose Logistics (our team)">
                  {team.admins.map((a) => (
                    <option key={a.id} value={a.id}>{a.full_name} — {a.role_label}</option>
                  ))}
                </optgroup>
                {team.company_people.filter((p) => !p.is_me).length > 0 ? (
                  <optgroup label={team.company_name || 'My company'}>
                    {team.company_people.filter((p) => !p.is_me).map((p) => (
                      <option key={p.id} value={p.id}>{p.full_name} — {p.role_label}</option>
                    ))}
                  </optgroup>
                ) : null}
              </select>

              {team.company_people.filter((p) => !p.is_me).length === 0 ? (
                <p className="text-xs mb-4" style={{ color: '#94A3B8' }}>
                  You are the only person from {team.company_name || 'your company'} on the portal so far. Ask us to invite colleagues,
                  or invite them yourself from My Profile → My Team, and they will appear here.
                </p>
              ) : null}

              <label className="block text-xs uppercase tracking-wide mb-1" style={{ color: '#94A3B8' }}>Subject</label>
              <input
                value={compose.subject}
                onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))}
                placeholder="What is it about?"
                className="w-full px-3 py-2 border rounded-lg text-sm mb-4 focus:outline-none"
                style={{ borderColor: '#E8ECEF' }}
              />

              <label className="block text-xs uppercase tracking-wide mb-1" style={{ color: '#94A3B8' }}>Message</label>
              <textarea
                value={compose.message}
                onChange={(e) => setCompose((c) => ({ ...c, message: e.target.value }))}
                rows={5}
                placeholder="Type your message..."
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none"
                style={{ borderColor: '#E8ECEF' }}
              />

              <div className="flex justify-end gap-2 mt-5">
                <button type="button" onClick={() => setComposeOpen(false)} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50" style={{ borderColor: '#E8ECEF' }}>
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                  style={{ backgroundColor: '#00A86B' }}
                >
                  {sending ? <FaSpinner className="animate-spin" size={12} /> : <FaPaperPlane size={12} />} Send
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

    </ClientLayout>
  );

}

export default ClientMessages;
